import { useState } from "react";
import { ArrowRight, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { emailContainsPlus } from "@/lib/utils";
import { Hint, KeyHint } from "../hints";
import { StepCard, StepCardLabel } from "../step-card";

export interface AccountDraft {
  firstName: string;
  lastName: string;
  email: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Returns the reason the email can't be used, or null if it can. */
function getEmailError(email: string): string | null {
  if (!EMAIL_PATTERN.test(email)) return "Enter a valid email address.";
  if (emailContainsPlus(email)) return "Email addresses containing '+' aren't allowed.";
  return null;
}

/**
 * The opening turn on /get-started — who they are. Submitting creates the
 * account; the code step that follows signs them in to it.
 */
export function AccountStep({
  defaultDraft,
  onSubmit,
  isSubmitting,
}: {
  defaultDraft?: AccountDraft;
  onSubmit: (draft: AccountDraft) => void;
  isSubmitting: boolean;
}) {
  const [firstName, setFirstName] = useState(defaultDraft?.firstName ?? "");
  const [lastName, setLastName] = useState(defaultDraft?.lastName ?? "");
  const [email, setEmail] = useState(defaultDraft?.email ?? "");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [touchedEmail, setTouchedEmail] = useState(false);

  const emailError = email ? getEmailError(email.trim()) : null;
  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    !emailError &&
    acceptedTerms &&
    !isSubmitting;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim().toLowerCase(),
    });
  };

  return (
    <StepCard>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <StepCardLabel>First name</StepCardLabel>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ada"
              autoComplete="given-name"
              className="h-11 text-[15px]"
              autoFocus
            />
          </div>
          <div>
            <StepCardLabel>Last name</StepCardLabel>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Lovelace"
              autoComplete="family-name"
              className="h-11 text-[15px]"
            />
          </div>
        </div>

        <div>
          <StepCardLabel>Work email</StepCardLabel>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouchedEmail(true)}
              placeholder="ada@northwind.vc"
              autoComplete="email"
              className="h-11 pl-9 text-[15px]"
              aria-invalid={touchedEmail && !!emailError}
            />
          </div>
          {touchedEmail && emailError ? (
            <p className="mt-1.5 text-xs text-destructive">{emailError}</p>
          ) : (
            <Hint className="mt-1.5">
              We'll email you a code to sign in — no password to remember.
            </Hint>
          )}
        </div>

        <label className="flex items-start gap-2.5 text-[13px] leading-snug text-muted-foreground">
          <Checkbox
            checked={acceptedTerms}
            onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
            className="mt-0.5"
            aria-label="Accept the terms and conditions and privacy policy"
          />
          <span>
            I accept the{" "}
            <a
              href="https://www.dgpt.io/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              terms and conditions
            </a>{" "}
            and{" "}
            <a
              href="https://www.dgpt.io/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              privacy policy
            </a>
          </span>
        </label>

        <div className="flex items-center justify-end gap-3 pt-1">
          <KeyHint keys={["↵"]} />
          <Button type="submit" disabled={!canSubmit} className="gap-2">
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Create account
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </form>
    </StepCard>
  );
}

/** Six-digit code from the sign-in email. */
export function VerifyCodeStep({
  email,
  onSubmit,
  onResend,
  onChangeEmail,
  isSubmitting,
}: {
  email: string;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onChangeEmail: () => void;
  isSubmitting: boolean;
}) {
  const [code, setCode] = useState("");
  const canSubmit = code.length === 6 && !isSubmitting;

  return (
    <StepCard>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(code);
        }}
      >
        <div>
          <StepCardLabel>Code sent to {email}</StepCardLabel>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="h-11 text-center font-mono text-lg tracking-[0.5em]"
            aria-label="Verification code"
            autoFocus
          />
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <button
              type="button"
              onClick={onResend}
              disabled={isSubmitting}
              className="underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              Resend code
            </button>
            <span aria-hidden>·</span>
            <button
              type="button"
              onClick={onChangeEmail}
              disabled={isSubmitting}
              className="underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              Use a different email
            </button>
          </div>

          <Button type="submit" disabled={!canSubmit} className="gap-2">
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Verify
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </form>
    </StepCard>
  );
}
